import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { PINNED_PRODUCT_ID } from "@/utils/collections";
import { formatBRL } from "@/utils/format";
import { useScrollLock } from "@/utils/useScrollLock";
import type { Product } from "@/mocks/products";

// Abre uma vez a cada vez que o site é aberto (carregamento da página), não a cada
// volta à página inicial dentro da mesma visita. Só é usado na página inicial.
let shownInThisVisit = false;
const OPEN_DELAY_MS = 1300; // depois da animação de abertura da loja

export default function KitEmpreendedorModal({ product }: { product?: Product }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  useScrollLock(open);

  useEffect(() => {
    if (shownInThisVisit) return;
    const timer = window.setTimeout(() => {
      shownInThisVisit = true;
      setOpen(true);
    }, OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const base = import.meta.env.BASE_URL;
  const goToProduct = () => {
    setOpen(false);
    navigate(`/produto/${PINNED_PRODUCT_ID}`);
  };

  return (
    <div
      onClick={() => setOpen(false)}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kit-empreendedor-title"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="relative max-h-[92dvh] w-full max-w-md overflow-y-auto rounded-2xl bg-background-50 shadow-2xl"
      >
        <button
          ref={closeRef}
          type="button"
          aria-label="Fechar"
          onClick={() => setOpen(false)}
          className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-background-50/90 text-foreground-700 shadow hover:bg-background-50"
        >
          <X size={18} />
        </button>
        <img
          src={`${base}banner-kit-empreendedor-mobile.webp`}
          alt="Kit Empreendedor: bandejas com brincos dourados e coloridos"
          width={1080}
          height={667}
          className="block h-auto w-full"
        />
        <div className="p-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary-700">Oportunidade</p>
          <h2 id="kit-empreendedor-title" className="font-heading mt-2 text-2xl leading-tight text-foreground-950">
            Kit Empreendedor
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            Brincos sortidos prontos para revender. Garanta já o seu kit e comece a lucrar agora mesmo!
          </p>
          {product && product.price > 0 && (
            <p className="mt-3 text-lg font-semibold text-foreground-950">
              {formatBRL(product.price)}
              {product.compareAtPrice && product.compareAtPrice > product.price && (
                <span className="ml-2 text-sm font-normal text-foreground-400 line-through">{formatBRL(product.compareAtPrice)}</span>
              )}
            </p>
          )}
          <button
            type="button"
            onClick={goToProduct}
            className="mt-5 w-full rounded-full bg-foreground-950 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
          >
            Quero meu Kit
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-2 w-full py-3 text-xs text-foreground-500 hover:text-foreground-700"
          >
            Agora não
          </button>
        </div>
      </div>
    </div>
  );
}
